/**
 * Guardar «Tu situación migratoria»: el PATCH /job-apply/api/preferences con
 * SOLO `h2_status` y `h2_previous_employer` y/o `eu_work_authorization` (ver
 * `lib/immigration`). Lo usan el paso del alta y las tarjetas de Preferencias.
 */
import { apiFetch } from '~/lib/api';
import { messages } from '~/i18n';
import { buildH2Payload, type EuWorkAuthorization, type H2Payload, type H2Status } from '../lib/immigration';
import type { JobPreferences } from '../lib/preferences';

/** Un error con el texto ya listo para mostrar (el del servidor si lo trae). */
export class ImmigrationSaveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImmigrationSaveError';
  }
}

export type ImmigrationPayload = Partial<H2Payload> & { eu_work_authorization?: EuWorkAuthorization };

/** Guarda y devuelve las preferencias que quedaron (las del servidor si las devuelve). */
export async function saveH2Status(status: H2Status, employer: string): Promise<JobPreferences | null> {
  return saveImmigration(buildH2Payload(status, employer));
}

/** Sólo los papeles de la UE (la tarjeta de Preferencias). */
export async function saveEuAuthorization(value: EuWorkAuthorization): Promise<JobPreferences | null> {
  return saveImmigration({ eu_work_authorization: value });
}

/** Un solo PATCH con lo que haya (el paso del alta puede llevar las dos preguntas). */
export async function saveImmigration(payload: ImmigrationPayload): Promise<JobPreferences | null> {
  const t = messages().onboarding.immigration;
  let res: Response;
  try {
    res = await apiFetch('/job-apply/api/preferences', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new ImmigrationSaveError(t.connectionError);
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ImmigrationSaveError(body?.error || t.saveFailed);
  return (body?.preferences as JobPreferences | undefined) ?? null;
}
